import React, {
    useCallback,
    useState,
    useMemo,
    useEffect,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';

import combineActions from '@app/utils/combineActions';
import getCollections from '@app/actions/collections/getCollections';
import getPosts from '@app/actions/media/getPosts';
import sendNotification from '@app/actions/notifications/send';
import addCollectionsToPosts from '@app/actions/media/addCollectionToPosts';
import removeCollectionFromPost from '@app/actions/media/removeCollectionFromPost';
import getFilters from '@app/selectors/getSelectedFilters';
import clearOverrideFilters from '@app/actions/media/clearOverrideFilters';
import setIsReturningFromOverride from '@app/actions/media/setIsReturningFromOverride';
import usePendo from '@app/components/hooks/usePendo';
import { exportCsv as exportCsvUtil } from '@app/utils/exportSmpToCsv';
import getTruthyKeys from '@app/utils/getTruthyKeys';
import { downloadImages } from '@app/broadcasts/utils/frameDownloadUtils';
import {
    getPostImageFilename,
    getPostImageUrl,
} from '@app/utils/generatePostReport';
import {
    expandCarouselPostRecords,
    expandCarouselPosts,
} from './carouselGroups';
import { commitCollectionAssignment } from './commitCollectionAssignment';

import { Client, User } from '@app/types/api';
import { Post } from './types';
import {
    buildSelectablePosts,
    buildSelectablePostsOrder,
    isPostSelectableForSelectionType,
} from './selectionHelpers';
import { Notification } from '@app/components/notifications/Notifications';

import {
    PREMIUM_POST_LEVEL_INSIGHTS,
    PREMIUM_COLLECTIONS,
    PREMIUM_SMP_BELOW_THIRTY_PERCENT,
    INFINITE_SCROLL_ACCESS,
    // FILTER_REDESIGN,
} from '@app/constants/config';

const defaultQuery = {
    pageSize: 100,
    sort: 'sponsorMediaValue',
    order: 'desc',
    page: 0,
    groupCarouselPosts: true,
};

const defaultTableColumns = {
    postLevelMetrics: true,
    organicAndPaidMetrics: false,
    showCollabPostInfo: false,
};

interface FetchPostsOptions {
    sort?: string;
    order?: string;
    page?: number;
}

type SelectedPostsState = Record<string, boolean>;
type SetSelectedPosts = React.Dispatch<
    React.SetStateAction<SelectedPostsState>
>;

export const SmpContext = React.createContext<SmpContextType>(null);

type ProviderProps = {
    children: React.ReactNode;
    filters: any;
    collections: any;
    client: Client;
    media: any;
    user: User;
    getPosts: typeof getPosts;
    getCollections: typeof getCollections;
    clearOverrideFilters: typeof clearOverrideFilters;
    setIsReturningFromOverride: typeof setIsReturningFromOverride;
    // Bound action signatures after connect/combineActions
    // removeCollectionFromPost is not used in this provider
    addCollectionsToPosts: (
        postIds: Array<number | string>,
        collectionIds: number[],
    ) => any;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
};

type SmpContextType = {
    isPremiumInsights: boolean;
    filtersOverride: any;
    revertFilters: () => void;
    onGoBackToInsights: () => void;
    isSelectMode: boolean;
    isPremiumCollections: boolean;
    openCollectionsModal: () => void;
    openDownloadModal: () => void;
    downloadJpegs: () => Promise<void>;
    isDownloadingJpegs: boolean;
    openSelectionMode: (option: string) => void;
    closeSelectionMode: () => void;
    exportCsv: () => Promise<void>;
    selectionType: string;
    selectedCount: number;
    isNonenSelected: boolean;
    isAllSelected: boolean;
    selectAll: () => void;
    unselectAll: () => void;
    view: string;
    setView: (view: string) => void;
    showExistingPosts: boolean;
    selectablePosts: any;
    selectablePostsOrder: any;
    currency: string;
    selectedPosts: SelectedPostsState;
    setSelectedPosts: SetSelectedPosts;
    displayDetails: (post: any) => void;
    collections: any;
    totals: any;
    tableColumns: any;
    infiniteScrollEnabled: boolean;
    postsOrder: any;
    scrollBottom: () => void;
    isLoading: boolean;
    hasPostsAvailable: boolean;
    morePages: boolean;
    nextPage: () => void;
    scrollTop: () => void;
    displayPostDetailsModal: boolean;
    posts: any;
    activePostId: any;
    closeDisplayDetails: (e: React.MouseEvent) => void;
    closeModalWithButton: () => void;
    clientId: any;
    isCollectionsModalOpen: boolean;
    closeCollectionsModal: () => void;
    onSubmitCollections: (
        clientId: any,
        collectionIds: any[],
        postIds: any[],
    ) => void;
    onErrorCollections: (
        clientId: any,
        collectionIds: any[],
        postIds: any[],
    ) => void;
    selectedArr: any[];
    isDownloadModalOpen: boolean;
    closeDownloadModal: () => void;
    onFinishDownload: () => void;
    postsSelected: Record<string, Post | undefined>;
    primaryColor: string;
    secondaryColor: string;
    isCsvDownloadModalOpen: boolean;
    closeCSVDownloadModal: () => void;
    refetchPosts: () => Promise<void>;
    setColumns: (col: any) => void;
    isSelectingColumns: boolean;
    setIsSelectingColumns: (isSelectingColumns: boolean) => void;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
    client: Client;
    hasCollabPostsReporting: boolean;
};

const Provider = ({
    children,
    filters,
    // collections,
    client,
    media,
    user,
    collections,
    getPosts,
    getCollections,
    clearOverrideFilters,
    setIsReturningFromOverride,
    addCollectionsToPosts,
    sendNotification,
}: ProviderProps) => {
    const [query, setQuery] = useState({
        ...defaultQuery,
        avgMvpScoreGreaterThan: client.permissions.includes(
            PREMIUM_SMP_BELOW_THIRTY_PERCENT,
        )
            ? 0
            : 0.3,
    });
    const [displayPostDetailsModal, setDisplayPostDetailsModal] =
        useState(false);
    const [activePostId, setActivePostId] = useState(null);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [isDownloadingJpegs, setIsDownloadingJpegs] = useState(false);
    const [hasChangedSortBy, setHasChangedSortBy] = useState(false);
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [isCollectionsModalOpen, setIsCollectionsModalOpen] = useState(false);
    const [isCsvDownloadModalOpen, setIsCsvDownloadModalOpen] = useState(false);
    const [isSelectingColumns, setIsSelectingColumns] = useState(false);
    const [selectionType, setSelectionType] = useState('assign');
    const [selectedPosts, setSelectedPosts] = useState<SelectedPostsState>({});
    const [tableColumns, setTableColumns] = useState(defaultTableColumns);
    const [view, setView] = useState('grid');
    const didMount = useRef(false);

    useEffect(() => {
        // Get client collections
        if (client.permissions.includes(PREMIUM_COLLECTIONS)) {
            getCollections(client.id);
        }
    }, []);

    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        if (!!query && isSubmittingForm) {
            _fetchPosts({
                sort: filters.sort,
                order: filters.order,
                page: query.page,
            });
            setIsSubmittingForm(false);
        }
    }, [query]);

    useEffect(() => {
        // Dont fetch if filters are not initialized, or if coming from Insights page
        if (!filters.changesId || media.filtersOverride) return;
        if (!filters.isLoading) {
            setQuery({ ...query, page: 0 });
            _fetchPosts({
                sort: filters.sort,
                order: filters.order,
                page: 0,
            });
            scrollTop();
        }
    }, [filters.changesId]);

    useEffect(() => {
        // Handles fetch posts when coming from Insights Page
        if (!filters.overridesId || !media.filtersOverride) return;
        if (!filters.isLoading) {
            setQuery({ ...query, page: 0 });
            _fetchPosts({
                sort: filters.sort,
                order: filters.order,
                page: 0,
            });
            scrollTop();
        }
    }, [filters.overridesId]);

    useEffect(() => {
        return () => {
            if (media.filtersOverride) {
                clearOverrideFilters();
            }
        };
    }, [media.filtersOverride]);

    const _fetchPosts = useCallback(
        (options: FetchPostsOptions = {}) => {
            const { ...params } = query;
            const sort = options.sort ? options.sort : params.sort;
            const order = options.order ? options.order : params.order;
            const page = options.page == null ? params.page : options.page;
            const addOrderBy = R.assoc('orderBy', [`${sort}-${order}`]);
            const setParams = R.pipe(addOrderBy, R.omit(['sort', 'order']));
            const queryParams = {
                ...setParams(params),
                page: page,
                groupCarouselPosts: true,
            };
            return getPosts(queryParams);
        },
        [client, query, user, getPosts],
    );

    const refetchPosts = useCallback(async () => {
        const currentPage = query.page;
        const sortOrder = {
            sort: filters.sort,
            order: filters.order,
        };

        await _fetchPosts({ ...sortOrder, page: 0 });

        for (let page = 1; page <= currentPage; page++) {
            await _fetchPosts({ ...sortOrder, page });
        }
    }, [_fetchPosts, query.page, filters.sort, filters.order]);

    const {
        posts,
        postsOrder,
        filtersOverride,
        isLoading,
        totals,
        hasMore: morePages,
    } = media;
    const { page } = query;
    const currency = client?.currency?.code;
    const clientId = client.id;
    const primaryColor = client.primaryColor;
    const secondaryColor = client.secondaryColor;
    const showExistingPosts = !isLoading || page > 0;

    const selectablePosts = useMemo(
        () => buildSelectablePosts(posts, selectionType),
        [posts, selectionType],
    );

    const selectablePostsOrder = useMemo(
        () => buildSelectablePostsOrder(postsOrder, posts, selectionType),
        [postsOrder, posts, selectionType],
    );

    const selectedArr = expandCarouselPosts(
        posts,
        getTruthyKeys(selectedPosts),
    );
    const selectedCount = selectedArr.length;
    const hasPostsAvailable = postsOrder.length > 0;
    const postsCount = selectablePostsOrder.length;
    const isNonenSelected = selectedCount === 0;
    const isAllSelected = selectedCount === postsCount;

    const { pendo } = usePendo();
    const navigate = useNavigate();

    const isPremiumInsights = client.permissions.includes(
        PREMIUM_POST_LEVEL_INSIGHTS,
    );
    const isPremiumCollections =
        client.permissions.includes(PREMIUM_COLLECTIONS);
    const infiniteScrollEnabled = client.permissions.includes(
        INFINITE_SCROLL_ACCESS,
    );
    const hasCollabPostsReporting = true;

    const trackSortBy = option => {
        if (!pendo) return;
        pendo.track('TrackSortBy', { option: option });
        setHasChangedSortBy(false);
    };

    const fetchFirstPage = useCallback(() => {
        const { sort } = query;
        setIsSubmittingForm(true);
        if (hasChangedSortBy) trackSortBy(sort);
        setQuery({ ...query, page: 0 });
    }, [query]);

    const onGoBackToInsights = () => {
        setIsReturningFromOverride();
        navigate(-1);
    };

    const revertFilters = () => {
        clearOverrideFilters();
        fetchFirstPage();
    };

    const openDownloadModal = () => setIsDownloadModalOpen(true);
    const closeDownloadModal = () => setIsDownloadModalOpen(false);
    const openCollectionsModal = () => setIsCollectionsModalOpen(true);
    const closeCollectionsModal = () => setIsCollectionsModalOpen(false);
    const openCSVDownloadModal = () => setIsCsvDownloadModalOpen(true);
    const closeCSVDownloadModal = () => setIsCsvDownloadModalOpen(false);

    const openSelectionMode = option => {
        setIsSelectMode(true);
        setSelectionType(option);
    };

    const closeSelectionMode = () => {
        setIsSelectMode(false);
        setSelectionType('assign');
        setSelectedPosts({});
    };

    const closeDisplayDetails = e => {
        if (e.currentTarget !== e.target) return;
        setDisplayPostDetailsModal(false);
        setActivePostId(null);
    };

    const closeModalWithButton = () => {
        setDisplayPostDetailsModal(false);
        setActivePostId(null);
    };

    const selectAll = useCallback(() => {
        const selectedPosts = selectablePostsOrder.reduce((acc, id) => {
            const post = selectablePosts[String(id)];
            if (isPostSelectableForSelectionType(post, selectionType)) {
                acc[id] = true;
            }
            return acc;
        }, {});
        setSelectedPosts(selectedPosts);
    }, [selectablePostsOrder, selectablePosts, selectionType]);

    const unselectAll = () => setSelectedPosts({});

    const displayDetails = post => {
        const { permissions = [] } = client;
        if (!permissions.includes(PREMIUM_POST_LEVEL_INSIGHTS)) return;
        setDisplayPostDetailsModal(true);
        setActivePostId(post.id);
    };

    const scrollBottom = () => {
        document.getElementById('bottom').scrollIntoView();
    };

    const scrollTop = () => {
        document.getElementById('top')?.scrollIntoView({ behavior: 'smooth' });
    };

    const nextPage = () => {
        setIsSubmittingForm(true);
        setQuery({ ...query, page: query.page + 1 });
    };

    const onSubmitCollections = (_clientId, collectionIds, postIds) => {
        return commitCollectionAssignment({
            posts,
            postIds,
            collectionIds,
            addCollectionsToPosts,
            closeModal: () => setIsCollectionsModalOpen(false),
            clearSelection: () => setSelectedPosts({}),
            notifySuccess: message =>
                sendNotification(message, { context: 'success' }),
        });
    };

    const onErrorCollections = (_clientId, _collectionIds, _postIds) => {
        // Explicitly mark params as used to satisfy eslint
        void _clientId;
        void _collectionIds;
        void _postIds;
        sendNotification(
            `Failed to assign posts to collections. Please try again.`,
            {
                context: 'danger',
            },
        );
    };

    const onFinishDownload = () => {
        setIsDownloadModalOpen(false);
        setSelectedPosts({});
        sendNotification('Posts Downloaded Successfully!', {
            context: 'success',
        });
    };

    const setColumns = col => {
        setTableColumns(col);
        setIsSelectingColumns(false);
    };

    const { t } = useTranslation('ownedSocial');

    const postsSelected = expandCarouselPostRecords(
        posts,
        getTruthyKeys(selectedPosts),
    );

    const downloadJpegs = async () => {
        setIsDownloadingJpegs(true);
        try {
            // Carousel slides can be videos; those resolve to a frame image.
            const images = await Promise.all(
                Object.values(postsSelected).map(async post => ({
                    url: await getPostImageUrl(post).catch(() => undefined),
                    filename: getPostImageFilename(post),
                })),
            );
            const failed = await downloadImages(images);
            if (failed === images.length) {
                sendNotification(
                    t('measurement:socialMediaExposure.downloadJpegsFailed'),
                    { context: 'danger' },
                );
                return;
            }
            setSelectedPosts({});
            if (failed > 0) {
                sendNotification(
                    t('measurement:socialMediaExposure.downloadJpegsPartial', {
                        count: failed,
                    }),
                    { context: 'warning' },
                );
            }
        } finally {
            setIsDownloadingJpegs(false);
        }
    };

    const exportCsv = async () => {
        return exportCsvUtil({
            media,
            filters,
            query,
            client,
            user,
            tableColumns,
            openCSVDownloadModal,
            closeCSVDownloadModal,
            getFilters,
            sendNotification,
            t,
        });
    };

    const providerValue = {
        isPremiumInsights,
        filtersOverride,
        revertFilters,
        onGoBackToInsights,
        isSelectMode,
        isPremiumCollections,
        openDownloadModal,
        downloadJpegs,
        isDownloadingJpegs,
        openCollectionsModal,
        openSelectionMode,
        closeSelectionMode,
        exportCsv,
        selectionType,
        selectedCount,
        isNonenSelected,
        isAllSelected,
        selectAll,
        unselectAll,
        view,
        setView,
        showExistingPosts,
        selectablePosts,
        selectablePostsOrder,
        currency,
        selectedPosts,
        setSelectedPosts,
        displayDetails,
        collections,
        totals,
        tableColumns,
        infiniteScrollEnabled,
        postsOrder,
        scrollBottom,
        isLoading,
        hasPostsAvailable,
        morePages,
        nextPage,
        scrollTop,
        displayPostDetailsModal,
        posts,
        activePostId,
        closeDisplayDetails,
        closeModalWithButton,
        clientId,
        isCollectionsModalOpen,
        closeCollectionsModal,
        onSubmitCollections,
        onErrorCollections,
        selectedArr,
        isDownloadModalOpen,
        closeDownloadModal,
        onFinishDownload,
        postsSelected,
        primaryColor,
        secondaryColor,
        isCsvDownloadModalOpen,
        closeCSVDownloadModal,
        refetchPosts,
        setColumns,
        isSelectingColumns,
        setIsSelectingColumns,
        sendNotification,
        client,
        hasCollabPostsReporting,
    };

    return (
        <SmpContext.Provider value={providerValue}>
            {children}
        </SmpContext.Provider>
    );
};

export const useSmpContext = () => {
    const context = React.useContext(SmpContext);
    if (!context) {
        throw new Error('useSmpContext must be used within a SmpProvider');
    }
    return context;
};

export default connect(
    ['media', 'filters', 'collections', 'client', 'user'],
    combineActions({
        getCollections,
        getPosts,
        clearOverrideFilters,
        setIsReturningFromOverride,
        removeCollectionFromPost,
        addCollectionsToPosts,
        sendNotification,
    }),
)(Provider);
